import React, { useState, useEffect, useRef, useContext } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import DraggableFieldType from "./DraggableFieldType";
import QuestionField from "components/UserScreen/QuestionField";
import {
  DragAndDrop,
  useDroppable,
  useDraggable,
} from "@progress/kendo-react-common";
import { addUserScreen, getWorkFlowInterviews, updateScreen } from "api";
import PropertiesInputField from "./PropertiesInputField";
import { MdCancel, MdOutlineMenu } from "react-icons/md";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { CiEdit } from "react-icons/ci";
import { Tile } from "components/UIComponents/Tile";
import { Loader } from "@progress/kendo-react-indicators";
import styles from "../CaseWorkflow.module.scss";

const DragContext = React.createContext([null, () => {}]);
const DropContext = React.createContext([null, () => {}]);
const DirectionContext = React.createContext(["before", () => {}]);

const fieldTypes = [
  "Label",
  "Text",
  "Combobox",
  "Date",
  "Dropdown",
  "MultilineText",
  "Integer",
  "Decimal",
  "Time",
  "DateTime",
  "CheckBox",
  "Radio",
];

const DEFAULT_FIELD_PROPERTIES = {
  label: "",
  variable: "",
  hint: "",
  length: 0,
  required: false,
  defaultValue: "",
  dataType: "",
  labelAboveField: true,
  gridWidth: 0,
  gridOffset: 0,
  active: true,
  minValue: "",
  maxValue: "",
  minLength: "",
  maxLength: "",
  symbol: "",
  helpText: "",
  choices: "",
  showLogic: "",
  choicesType: "",
  cssClass: "",
};
const DropZone = ({ element, onDrop, children }) => {
  const handleDragEnter = () => {
    element.current.style.backgroundColor = "#f5f5f5";
  };

  const handleDragLeave = () => {
    element.current.style.backgroundColor = "";
  };

  useDroppable(element, {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: onDrop,
  });

  return children;
};

const DroppableBox = ({
  question,
  setQuestion,
  fieldIndex,
  onClick,
  selectedFieldIndex,
  onDrop,
  setSelectedFieldIndex,
  onDragStart,
  children,
  isEmptyState = false,
}) => {
  const element = useRef(null);
  const hintRef = useRef(null);
  const [drag] = useContext(DragContext);
  const [drop, setDrop] = useContext(DropContext);
  const [direction] = useContext(DirectionContext);
  const field = question[0]?.pages[0]?.userScreenFields[fieldIndex];
  const [position, setPosition] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Make the field draggable
  useDraggable(
    element,
    {
      onPress: (event) => {
        setOffset({ x: event.offsetX, y: event.offsetY });
        onDragStart();
      },
      onDrag: (event) => {
        setPosition({
          x: event.clientX - offset.x,
          y: event.clientY - offset.y,
        });
      },
      onDragEnd: () => {
        setPosition(null);
        setOffset({ x: 0, y: 0 });
      },
    },
    { hint: hintRef, autoScroll: false }
  );

  const handleDragEnter = () => {
    setDrop({
      element: element,
      data: { id: fieldIndex, type: "field" },
    });
  };

  const handleDragLeave = () => {
    setDrop(null);
  };

  useDroppable(element, {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: () => onDrop(isEmptyState ? 0 : fieldIndex),
  });

  const handleRemove = (e) => {
    e.stopPropagation();
    const newQuestion = [...question];
    const field = newQuestion[0]?.pages[0]?.userScreenFields[fieldIndex];

    if (field) {
      // Instead of removing it, just mark inactive
      field.active = false;
    }

    setQuestion(newQuestion);
    if (selectedFieldIndex === fieldIndex) {
      setSelectedFieldIndex(null);
    }
  };
  return (
    <>
      <div
        ref={element}
        style={{
          borderBottom: "1px solid lightgray",
          borderLeft: selectedFieldIndex === fieldIndex && "4px solid #162EFF",
          backgroundColor:
            selectedFieldIndex === fieldIndex ? "#F0F1FB" : "white",
          display: drag?.index === fieldIndex ? "none" : undefined,
        }}
        className="d-flex justify-content-center align-items-center p-2"
        onClick={() => onClick(fieldIndex)}
      >
        <div style={{ width: "40px" }}>
          {selectedFieldIndex === fieldIndex && <MdOutlineMenu size={25} />}
        </div>

        <div className="px-1 w-100">
          {field ? (
            <QuestionField
              question={question[0]}
              field={field}
              userScreenFieldId={field?.userScreenFieldId}
              isSubmitted={false}
              fieldIndex={fieldIndex}
              fromScreenDesigner={true}
            />
          ) : (
            children
          )}
        </div>

        <div className="p-2 cursor-pointer" onClick={(e) => handleRemove(e)}>
          <MdCancel color="red" size={25} />
        </div>
      </div>

      {drag?.index === fieldIndex && position && (
        <div
          ref={hintRef}
          className="d-flex justify-content-center align-items-center p-2"
          style={{
            position: "fixed",
            left: position.x,
            top: position.y,
            zIndex: 1000,
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            backgroundColor: "#F0F1FB",
            borderLeft: "4px solid #162EFF",
            pointerEvents: "none",
          }}
        >
          <div style={{ width: "40px" }}>
            <MdOutlineMenu size={25} />
          </div>
          <div className="px-1 w-100">
            {field ? (
              <QuestionField
                question={question[0]}
                field={field}
                userScreenFieldId={field?.userScreenFieldId}
                isSubmitted={false}
                fieldIndex={fieldIndex}
                fromScreenDesigner={true}
              />
            ) : (
              children
            )}
          </div>
        </div>
      )}
    </>
  );
};

const ScreenDesigner = (props) => {
  const [question, setQuestion] = useState([
    {
      name: "",
      details: "",
      active: true,
      pages: [
        {
          heading: "",
          subQuestion: "",
          order: 0,
          active: true,
          userScreenFields: [],
        },
      ],
    },
  ]);

  const [isLoading, setIsLoading] = useState(!!props.screenId);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [drag, setDrag] = useState(null);
  const [drop, setDrop] = useState(null);
  const [direction, setDirection] = useState("before");
  const [dragType, setDragType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const currentField =
    question[0]?.pages[0]?.userScreenFields[selectedFieldIndex];

  const properties = [
    { label: "Label", property: "label" },
    { label: "Hint", property: "hint" },
    { label: "Variable", property: "variable", type: "number" },
    { label: "Default Value", property: "defaultValue" },
    { label: "Grid Width", property: "gridWidth", type: "number" },
    { label: "Grid Offset", property: "gridOffset", type: "number" },
    { label: "Length (px)", property: "length", type: "number" },
    { label: "Help Text", property: "helpText" },
    { label: "CSS Class", property: "cssClass" },
    {
      label: "Items",
      property: "choices",
      type: "multiline",
      showFor: ["Dropdown", "Combobox"],
    },
  ];

  const booleanComboBox = [
    { label: "Label Above Field", property: "labelAboveField" },
    { label: "Required", property: "required" },
  ];

  const handleFieldTypeDragStart = (type) => {
    setDragType("fieldType");
    setDrag({ type: "fieldType", data: type });
    setIsDragging(true);
  };

  const handleExistingFieldDragStart = (index) => {
    setDragType("existingField");
    setDrag({ type: "existingField", index });
    setSelectedFieldIndex(index);
    setIsDragging(true);
  };

  const handleDrop = (dropIndex) => {
    if (!drag || !drop) return;

    setQuestion((prev) => {
      const newQuestion = JSON.parse(JSON.stringify(prev));
      const fields = newQuestion[0]?.pages[0]?.userScreenFields || [];

      if (dragType === "fieldType") {
        const newField = {
          ...DEFAULT_FIELD_PROPERTIES,
          dataType: drag.data,
          order: fields.length,
        };

        // Insert at drop position or append if dropping on empty container
        if (dropIndex >= 0 && dropIndex <= fields.length) {
          fields.splice(dropIndex, 0, newField);
        } else {
          fields.push(newField);
        }
        setSelectedFieldIndex(dropIndex >= 0 ? dropIndex : fields.length - 1);
      } else if (dragType === "existingField") {
        // Reordering existing field
        if (drag.index === dropIndex) return prev; // No change if dropped on itself

        const [draggedField] = fields.splice(drag.index, 1);

        // Calculate adjusted drop position
        let adjustedDropIndex = dropIndex;
        if (dropIndex > drag.index) {
          adjustedDropIndex = dropIndex - 1;
        }

        // Ensure we don't drop out of bounds
        adjustedDropIndex = Math.max(
          0,
          Math.min(adjustedDropIndex, fields.length)
        );

        fields.splice(adjustedDropIndex, 0, draggedField);
        setSelectedFieldIndex(adjustedDropIndex);
      }

      // Update orders
      fields.forEach((field, idx) => {
        field.order = idx + 1;
      });

      return newQuestion;
    });

    setIsDragging(false);
  };

  const calculateDirection = (dragElement, dropElement) => {
    if (!dragElement || !dropElement) return "before";
    const dragRect = dragElement.getBoundingClientRect();
    const dropRect = dropElement.getBoundingClientRect();
    const dragCenterY = dragRect.top + dragRect.height / 2;
    const dropCenterY = dropRect.top + dropRect.height / 2;
    return dragCenterY < dropCenterY ? "before" : "after";
  };

  const droppableContainerRef = useRef(null);

  const handleContainerDrop = () => {
    if (!drag || dragType !== "fieldType") return;

    setQuestion((prev) => {
      const newQuestion = JSON.parse(JSON.stringify(prev));
      const fields = newQuestion[0]?.pages[0]?.userScreenFields || [];

      const newField = {
        ...DEFAULT_FIELD_PROPERTIES,
        dataType: drag.data,
        order: fields.length + 1,
      };

      // Add to end of list when dropping on container
      newQuestion[0].pages[0].userScreenFields = [...fields, newField];
      return newQuestion;
      // When dropping on empty container, add to end
    });

    setSelectedFieldIndex(question[0]?.pages[0]?.userScreenFields?.length || 0);
  };

  useEffect(() => {
    if (drag?.element?.current && drop?.element?.current) {
      const newDirection = calculateDirection(
        drag.element.current,
        drop.element.current
      );
      setDirection(newDirection);
    }
  }, [drag, drop]);

  useEffect(() => {
    if (
      question[0]?.pages[0]?.userScreenFields?.length - 1 <
      selectedFieldIndex
    ) {
      setSelectedFieldIndex(0);
    }
  }, [question]);

  useEffect(() => {
    const getViewWorkFlows = async () => {
      try {
        const response = await getWorkFlowInterviews(props.screenId);
        if (response?.data?.data) {
          if (!response?.data?.data?.pages?.length) {
            response.data?.data?.pages?.push({
              heading: "",
              subQuestion: "",
              order: 0,
              active: true,
              userScreenFields: [],
            });
          }
          setQuestion([response?.data?.data]);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    if (props.screenId) getViewWorkFlows();
  }, []);

  const handleInputChange = (property) => (e) => {
    let value = e.target.value;
    if (selectedFieldIndex === null) return;
    setQuestion((prevQuestion) => {
      const newQuestion = JSON.parse(JSON.stringify(prevQuestion));
      if (property === "choices") {
        newQuestion[0].pages[0].userScreenFields[selectedFieldIndex][property] =
          value.split("\n").filter((item) => item.trim() !== "");
        newQuestion[0].pages[0].userScreenFields[
          selectedFieldIndex
        ].choicesType = 0;
        // Store the raw text value for the textarea
        newQuestion[0].pages[0].userScreenFields[
          selectedFieldIndex
        ].choicesText = value;
      } else {
        newQuestion[0].pages[0].userScreenFields[selectedFieldIndex][property] =
          value;
      }
      return newQuestion;
    });
  };

  const handleSave = async () => {
    try {
      if (props.screenId) {
        await updateScreen(question[0], props.screenId);
      } else {
        await addUserScreen({
          ...question[0],
          type: props.screenType,
        });
      }

      props.onClose && props.onClose(true, question[0]);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog
      className="dialog-xxl"
      title="Screen Designer"
      onClose={props.onClose}
    >
      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center mt-5">
          <Loader themeColor={"warning"} size={"large"} type={"pulsing"} />
        </div>
      ) : (
        <div id="main" className="h-100">
          <div
            className="row m-0 p-0 overflow-auto mh-100"
            style={{ height: "calc(100% - 65px)" }}
          >
            <DragContext.Provider value={[drag, setDrag]}>
              <DropContext.Provider value={[drop, setDrop]}>
                <DirectionContext.Provider value={[direction, setDirection]}>
                  <DragAndDrop>
                    <div className="col-12 col-xl-2 p-0 mh-100 overflow-hidden">
                      <div className="h-100 p-3 pe-1">
                        <Tile
                          className="px-3 overflow-auto h-100"
                          heading="Fields"
                        >
                          {fieldTypes.map((fieldType, index) => (
                            <DraggableFieldType
                              key={index}
                              fieldType={fieldType}
                              onDragStart={handleFieldTypeDragStart}
                            />
                          ))}
                        </Tile>
                      </div>
                    </div>

                    <div className="col-8 col-xl-7 p-0 mh-100">
                      <div className="h-100 px-1 py-3 mh-100">
                        <div
                          ref={droppableContainerRef}
                          className="h-100 p-2 overflow-auto mh-100  pb-5"
                          style={{
                            borderRadius: "10px",
                            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
                            minHeight: "300px",
                          }}
                        >
                          <DropZone
                            element={droppableContainerRef}
                            onDrop={handleContainerDrop}
                          >
                            <div id="heading" className="px-5">
                              <CiEdit size={25} />
                              <Input
                                className="ps-3 fw-bold fs-md border-0"
                                style={{ minWidth: "calc(100% - 25px)" }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setQuestion((prev) => {
                                    const updatedQuestion = [...prev];
                                    updatedQuestion[0].name = value;
                                    return updatedQuestion;
                                  });
                                }}
                                placeholder="Module Name"
                                value={question[0].name}
                              />
                              <div
                                style={{
                                  borderBottom: "1px solid #d3d3d3",
                                  marginBottom: "10px",
                                }}
                              />
                            </div>

                            <div id="subheading" className="px-5">
                              <CiEdit size={20} />
                              <Input
                                className="ps-3 fw-bold border-0"
                                style={{ minWidth: "calc(100% - 25px)" }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setQuestion((prev) => {
                                    const updatedQuestion = [...prev];
                                    if (updatedQuestion[0]?.pages[0]) {
                                      updatedQuestion[0].pages[0].heading =
                                        value;
                                    }
                                    return updatedQuestion;
                                  });
                                }}
                                value={question[0]?.pages[0]?.heading}
                                placeholder="Question Text"
                              />
                              <div
                                className="mb-3"
                                style={{
                                  borderBottom: "1px solid #d3d3d3",
                                  marginBottom: "10px",
                                }}
                              />
                            </div>

                            {question[0]?.pages[0]?.userScreenFields.length ===
                              0 && (
                              <DroppableBox
                                question={question}
                                setQuestion={setQuestion}
                                fieldIndex={0}
                                onClick={setSelectedFieldIndex}
                                selectedFieldIndex={selectedFieldIndex}
                                onDrop={handleDrop}
                                setSelectedFieldIndex={setSelectedFieldIndex}
                                onDragStart={() => {}}
                                isEmptyState={true}
                              >
                                <div className="text-center p-4 text-muted">
                                  Drag and drop fields here
                                </div>
                              </DroppableBox>
                            )}

                            {question[0]?.pages[0]?.userScreenFields.map(
                              (field, index) =>
                                field.active ? (
                                  <DroppableBox
                                    key={field.userScreenFieldId || index}
                                    question={question}
                                    setQuestion={setQuestion}
                                    fieldIndex={index}
                                    onClick={setSelectedFieldIndex}
                                    selectedFieldIndex={selectedFieldIndex}
                                    onDrop={handleDrop}
                                    setSelectedFieldIndex={
                                      setSelectedFieldIndex
                                    }
                                    onDragStart={() =>
                                      handleExistingFieldDragStart(index)
                                    }
                                  />
                                ) : null
                            )}
                          </DropZone>
                        </div>
                      </div>
                    </div>
                  </DragAndDrop>
                </DirectionContext.Provider>
              </DropContext.Provider>
            </DragContext.Provider>

            <div
              id="properties"
              className="col-4 col-xl-3 p-0 mh-100"
              style={{ zIndex: 3 }}
            >
              <div className="h-100 p-3 ps-1">
                <Tile
                  heading="Text Box Properties"
                  className="overflow-auto h-100"
                  headingClass="px-4"
                >
                  {currentField && (
                    <div className="px-3">
                      {properties.map(({ label, property, type, showFor }) => {
                        if (currentField.dataType === "Label") {
                          if (
                            property === "hint" ||
                            property === "variable" ||
                            property === "defaultValue" ||
                            property === "length" ||
                            property === "helpText"
                          ) {
                            return null;
                          }
                        }

                        const filterProperties =
                          currentField.dataType === "CheckBox" ||
                          currentField.dataType === "Radio" ||
                          currentField.dataType === "Dropdown" ||
                          currentField.dataType === "Combobox" ||
                          currentField.dataType === "Date" ||
                          currentField.dataType === "Time";

                        if (property === "hint" && filterProperties)
                          return null;

                        if (
                          property === "choices" &&
                          !showFor?.includes(currentField.dataType)
                        ) {
                          return null;
                        }
                        return (
                          <PropertiesInputField
                            key={property}
                            label={label}
                            type={type}
                            onChange={handleInputChange(property)}
                            value={currentField?.[property] ?? ""}
                            field={currentField}
                          />
                        );
                      })}

                      {booleanComboBox.map(({ label, property }) => {
                        if (
                          currentField.dataType === "Label" &&
                          (property === "labelAboveField" ||
                            property === "required")
                        ) {
                          return null;
                        }

                        return (
                          <div
                            className="d-flex w-100 align-items-center pb-2"
                            key={property}
                          >
                            <label className="w-50 fw-semibold fs-sm pe-1">
                              {label}
                            </label>
                            <DropDownList
                              data={[true, false]}
                              onChange={handleInputChange(property)}
                              value={
                                currentField?.[property] === true
                                  ? "True"
                                  : currentField?.[property] === false
                                  ? "False"
                                  : ""
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Tile>
              </div>
            </div>
          </div>

          <div id="footer" className="row dialog-footer m-0">
            <div className="d-flex justify-content-end">
              <Button
                className="common-btn p-3 bgBtnPrimary align-self-end border-0 me-3"
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};
export default ScreenDesigner;
